import { DateConversion } from "../DateConversion";

/**saveFileToOs
 * ------------
 * This helper takes a string and saves it to the OS. Note there is no
 * UI included in the journey; the user will see their browser act as
 * if they had clicked on a download link (i.e. for Windows Chrome, the
 * browser will show the file being downloaded). Any popups to confirm
 * download and filename should occur before saveFileToOs is called.
 *
 * Also note that the Blob type is hardcoded to "text/plain" for now,
 * to keep functionality clean (and to avoid malicious filetypes!).
 *
 * exportAsCsv
 * -----------
 * This helper creates a comma separated string and uses saveFileToOs to
 * export it to the user's hard drive as a .csv file.
 * The header array defines the properties to be exported ('prop') and
 * the column header strings ('label'). The type defines what the prop
 * is ('string' or 'unixDate'). Type 'string' means the property value
 * can be exported as is, and any other value means the value will need
 * some pre-processing (such as unixDate, which needs reformatting to a
 * human-readable date).
 *
 * Example:
 * A typical csv file contents is shown below. It can be created and saved
 * to the user's hard drive via
 * exportAsCsv(headers, <someDataArray>, <someFilenameString>);
 *
 * Phone Number,Description,Created On,Modified On
 * +919168695552,Abhishek Number,18 Jan 2021,Invalid Date
 * +12402325361,amit ,17 Jan 2022,20 Oct 2022
 * +919513954925,Amit Global,01 Oct 2020,18 Jul 2023
 *
 * The Headers object used to form this .csv is;
 *
 *  [
 *    {
 *      prop: "phoneNumber",
 *      label: "Phone Number",
 *      type: "string",
 *    },
 *    {
 *      prop: "description",
 *      label: "description",
 *      type: "string",
 *    },
 *    {
 *      prop: "createdOn",
 *      label: "Created On",
 *      type: "unixDate",
 *    },
 *    {
 *      prop: "modifiedOn",
 *      label: "Modified On",
 *      type: "unixDate",
 *    },
 *  ];
 *
 * The <someDataArray> must be an array of objects, each of which contain
 * properties phoneNumber, description, createdOn, modifiedOn.
 *
 * The <someFilenameString> is the full filename (including the fileType, so
 * for example 'export.csv'). No checks are made on this string for correctness;
 * it is assumed the calling code has already done validation.
 *
 * Note that to create a new type, you not only have to define a conversion
 * function, but also alternative text if the conversion fails (see code that
 * outputs 'Invalid Date' and '?') in exportAsCsv.
 *
 */

interface Header {
  prop: string;
  label: string;
  type: "string" | "unixDate";
}
type ExportAsCsvHeaders = Array<Header>;
type DataObj = Record<string, unknown>;
type DataObjs = Array<DataObj>;

const saveFileToOs = (content: string, filename: string): string => {
  // To avoid CORS and browser compatibility (window.saveAs etc), we
  // use the universal 'create a download link and auto-click it' method.
  //
  // Create file from content...
  const file = new Blob([content], { type: "text/plain" });
  // then create a download link for the file and auto-click it...
  const link = document.createElement("a");
  link.href = URL.createObjectURL(file);
  link.download = filename;
  link.click();
  // ... and finally, cleanup.
  URL.revokeObjectURL(link.href);
  link.remove();
  // Get the file size (for activity logs).
  const filesizeBytes = file.size | 0;
  const filesizeKbytes =
    filesizeBytes > 0 ? Number(filesizeBytes / 1024).toFixed(2) : "0";
  return `${filesizeKbytes}Kb`;
};

const exportAsCsv = (
  headers: ExportAsCsvHeaders,
  data: DataObjs,
  fileName: string,
  noDateErrorStr = "?",
  noDataErrorStr = "?"
) => {
  // Create the exported headers row...
  const headerRow = headers
    .map((item: Header) => item.label)
    .join(",")
    .concat("\n");
  // Create the exported data rows...
  const rows: Array<string> = data.map((item) => {
    const row: Array<string> = [];
    headers.forEach((header) => {
      if (item[header.prop] !== undefined) {
        // Value was found in current object, add it to the exported csv data.
        if (header.type === "unixDate") {
          // Its a unix date; convert to a readable date.
          row.push(DateConversion(item[header.prop] as number));
        } else {
          // Its a string/number; add it to the csv as a string.
          row.push((item[header.prop] as string | number).toString());
        }
      } else {
        // Value was expected but not found.
        if (header.type === "unixDate") {
          // Its a missing date; add no-date error string the csv.
          row.push(noDateErrorStr);
        } else {
          // Its a missing entry; add no-data error string to the csv.
          row.push(noDataErrorStr);
        }
      }
    });
    // Turn the row array into a comma separated string.
    return row.join(",");
  });
  // To create our final string, add the header as a the first line, and then
  // the rows (with a line return at the end of each row)
  const exportedData = `${headerRow}${rows.join("\n")}`;
  const fileSize = saveFileToOs(exportedData, fileName);

  return {
    exportedData,
    fileName,
    fileSize,
  };
};

export { exportAsCsv, saveFileToOs };
export type { ExportAsCsvHeaders };
